import { Menu } from 'antd';
import styled from 'styled-components';
import { COLOR } from '@/constants';

/**
 * Sidebar nav scale. Kept as named steps so parents/children stay on one
 * rhythm instead of drifting to arbitrary px values.
 */
const NAV = {
  itemHeight: 44,
  childHeight: 40,
  fontSize: 14,
  childFontSize: 13,
  iconSize: 16,
  childIconSize: 14,
  radius: 6,
  gutter: 8,
  /**
   * Left inset of a nested item, and its right padding.
   *
   * One pair of values rather than the three overlapping declarations these
   * replace: a 14px left margin, a `padding-inline` setting a 12px left, and a
   * `padding-left: 28px` that silently overrode it — so the number that
   * applied was never the one being read. 28 keeps the child's text a clear
   * step in from its parent's without relying on antd's own default padding,
   * which is themeable and not ours to assume.
   */
  childPadLeft: 28,
  childPadRight: 8,
  /** Accent bar drawn as an inset shadow so selection never shifts the label. */
  accentWidth: 3
} as const;

/** Landmark wrapper; takes the flex growth the menu used to own directly. */
export const NavLandmark = styled.nav`
  flex: 1;
  min-height: 0;
`;

export const StyledMenu = styled(Menu).attrs({ theme: 'dark' as const })`
  /* Deliberately auto, not a hard 100%: antd sets its own width on a collapsed
     inline menu to lay the icons out as a rail, and a fixed full width
     overrode it — so the menu stayed full-width inside the 80px sider and the
     icon-only layout never took effect. */
  width: auto;
  font-weight: 400;
  background: transparent !important;
  border-inline-end: none !important;
  padding: 0 0 200px;

  /* Top-level items & group headers */
  > .ant-menu-item,
  > .ant-menu-submenu > .ant-menu-submenu-title {
    display: flex;
    align-items: center;
    margin: 2px ${NAV.gutter}px !important;
    width: calc(100% - ${NAV.gutter * 2}px) !important;
    height: auto !important;
    min-height: ${NAV.itemHeight}px !important;
    line-height: 1.4 !important;
    font-size: ${NAV.fontSize}px;
    border-radius: ${NAV.radius}px;
    color: ${COLOR.sider_text} !important;
    transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

    a {
      color: inherit !important;
    }

    .ant-menu-item-icon,
    .anticon {
      color: ${COLOR.sider_text_muted} !important;
      font-size: ${NAV.iconSize}px;
    }
  }

  > .ant-menu-item:hover,
  > .ant-menu-submenu > .ant-menu-submenu-title:hover {
    background: ${COLOR.sider_item_hover} !important;
    color: ${COLOR.white} !important;

    .ant-menu-item-icon,
    .anticon {
      color: ${COLOR.white} !important;
    }
  }

  /* Keyboard focus must stay visible — antd removes the UA outline by default. */
  > .ant-menu-item:focus-visible,
  > .ant-menu-submenu > .ant-menu-submenu-title:focus-visible,
  .ant-menu-sub .ant-menu-item:focus-visible {
    outline: 2px solid ${COLOR.white} !important;
    outline-offset: -2px;
  }

  > .ant-menu-item-selected {
    background: ${COLOR.sider_item_active} !important;
    color: ${COLOR.white} !important;
    font-weight: 600;
    /* Inset shadow instead of border-left: no padding compensation, no 1px jump. */
    box-shadow: inset ${NAV.accentWidth}px 0 0 ${COLOR.casa};

    a {
      color: ${COLOR.white} !important;
    }

    .ant-menu-item-icon,
    .anticon {
      color: ${COLOR.hacienda} !important;
    }
  }

  .ant-menu-submenu-selected > .ant-menu-submenu-title {
    color: ${COLOR.white} !important;

    .ant-menu-item-icon,
    .anticon {
      color: ${COLOR.lavanda} !important;
    }
  }

  /* Nested dropdown items — one step down from the parent, not a size cliff */
  .ant-menu-sub {
    background: ${COLOR.sider_submenu_bg} !important;
    padding: 2px 0 4px !important;

    .ant-menu-item {
      display: flex;
      align-items: center;
      margin: 1px ${NAV.gutter}px !important;
      width: calc(100% - ${NAV.gutter * 2}px) !important;
      height: auto !important;
      min-height: ${NAV.childHeight}px !important;
      line-height: 1.4 !important;
      /* Indented from the parent's own inset, so the step is the one named in
         NAV rather than whatever three overlapping rules happened to total. */
      padding-inline: ${NAV.childPadLeft}px ${NAV.childPadRight}px !important;
      font-size: ${NAV.childFontSize}px !important;
      font-weight: 400;
      border-radius: ${NAV.radius - 2}px;
      color: ${COLOR.sider_text_muted} !important;
      transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

      a {
        color: inherit !important;
        font-size: inherit !important;
      }

      .ant-menu-item-icon,
      .anticon {
        color: ${COLOR.sider_text_muted} !important;
        font-size: ${NAV.childIconSize}px !important;
        min-width: ${NAV.childIconSize}px !important;
      }
    }

    .ant-menu-item:hover {
      background: ${COLOR.sider_item_hover} !important;
      color: ${COLOR.white} !important;

      .ant-menu-item-icon,
      .anticon {
        color: ${COLOR.white} !important;
      }
    }

    .ant-menu-item-selected {
      background: ${COLOR.sider_item_active} !important;
      color: ${COLOR.white} !important;
      font-weight: 600;
      box-shadow: inset ${NAV.accentWidth - 1}px 0 0 ${COLOR.casa};

      a {
        color: ${COLOR.white} !important;
      }

      .ant-menu-item-icon,
      .anticon {
        color: ${COLOR.hacienda} !important;
      }
    }
  }

  .ant-menu-submenu-arrow {
    color: ${COLOR.sider_text_muted} !important;
    font-size: 10px !important;
  }

  .ant-menu-submenu-open > .ant-menu-submenu-title .ant-menu-submenu-arrow {
    color: ${COLOR.lavanda} !important;
  }

  /*
   * Collapsed rail: icons centred, keeping a full-size hit area.
   *
   * The width and padding have to be restated because the expanded rules above
   * set them with !important — an inset of two gutters makes sense beside a
   * label and leaves an icon no room once the sider is 80px wide. The label is
   * hidden outright rather than left to overflow: antd keeps it in the DOM as
   * the tooltip source, so hiding it visually costs nothing and stops it
   * pushing the icon off centre.
   */
  &.ant-menu-inline-collapsed {
    > .ant-menu-item,
    > .ant-menu-submenu > .ant-menu-submenu-title {
      justify-content: center;
      padding-inline: 0 !important;
      width: calc(100% - ${NAV.gutter * 2}px) !important;
    }

    /* The anchor carries the label; the icon is a sibling of it. */
    > .ant-menu-item a,
    > .ant-menu-submenu > .ant-menu-submenu-title .ant-menu-title-content {
      display: none !important;
    }

    .ant-menu-item-icon,
    .anticon {
      /* Margin-free so the icon sits on the rail's centre line rather than
         offset by the gap it would leave for a label. */
      margin-inline-end: 0 !important;
      font-size: ${NAV.iconSize + 2}px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    > .ant-menu-item,
    > .ant-menu-submenu > .ant-menu-submenu-title,
    .ant-menu-sub .ant-menu-item {
      transition: none;
    }
  }
`;
